import { MOVE_NEEDS_DELETE_REASON } from "../components/BpaImport/importText";
import { ALL_KINDS, type ImportKind, type ImportMode } from "./bpaImport";
import { moveBlockedReason } from "./bpaImportEditionStatus";

export type ImportPurpose = "split" | "merge";

export type ImportUrlValues = {
  sourceEditionBpaId: string | null;
  sourceEditionId: string | null;
  kinds: ImportKind[];
  mode: ImportMode;
  purpose?: ImportPurpose | null;
  hub?: string | null;
};

export type ImportUrlContext = {
  targetEditionBpaId: string;
  targetEditionId: string;
  offeredSourceIds: string[];
  sourceEditionStatus: string | null;
  canDelete: boolean;
  isHubForImport: (
    hub: string,
    importing: {
      purpose: ImportPurpose;
      sourceEditionBpaId: string | null;
      targetEditionBpaId: string;
    },
  ) => boolean;
};

export type ResolvedImportUrl = {
  sourceEditionBpaId: string | null;
  sourceEditionId: string | null;
  kinds: ImportKind[];
  mode: ImportMode;
  purpose: ImportPurpose | null;
  hub: string | null;
  notice: string | null;
  blocked: { reason: string; hub: string } | null;
};

export function writeImportUrl(values: ImportUrlValues, targetEditionId: string): URLSearchParams {
  const params = new URLSearchParams();
  if (values.sourceEditionBpaId) params.set("source", values.sourceEditionBpaId);
  if (values.sourceEditionId && values.sourceEditionId !== targetEditionId) {
    params.set("sourceEdition", values.sourceEditionId);
  }
  params.set("kinds", values.kinds.join(","));
  params.set("mode", values.mode);
  if (values.purpose && values.hub) {
    params.set("purpose", values.purpose);
    params.set("hub", values.hub);
  }
  return params;
}

function readKinds(raw: string | null): ImportKind[] {
  if (raw === null) return ALL_KINDS;
  if (raw === "") return [];
  const requested = raw.split(",");
  const kinds = ALL_KINDS.filter(kind => requested.includes(kind));
  return kinds.length > 0 ? kinds : ALL_KINDS;
}

function readPurpose(raw: string | null): ImportPurpose | null {
  return raw === "split" || raw === "merge" ? raw : null;
}

function moveBlockedFor(context: ImportUrlContext): string | null {
  return (
    moveBlockedReason(context.sourceEditionStatus) ??
    (context.canDelete ? null : MOVE_NEEDS_DELETE_REASON)
  );
}

export function resolveImportUrl(
  params: URLSearchParams,
  context: ImportUrlContext,
): ResolvedImportUrl {
  const requestedSource = params.get("source");
  const sourceEditionBpaId =
    requestedSource && context.offeredSourceIds.includes(requestedSource) ? requestedSource : null;
  const sourceEditionId = params.get("sourceEdition") || context.targetEditionId;

  const requestedPurpose = readPurpose(params.get("purpose"));
  const requestedHub = params.get("hub");
  const hubAccepted =
    requestedPurpose !== null &&
    !!requestedHub &&
    context.isHubForImport(requestedHub, {
      purpose: requestedPurpose,
      sourceEditionBpaId,
      targetEditionBpaId: context.targetEditionBpaId,
    });
  const purpose = hubAccepted ? requestedPurpose : null;
  const hub = hubAccepted ? requestedHub : null;
  const kinds = purpose ? ALL_KINDS : readKinds(params.get("kinds"));

  const wantsMove = purpose !== null || params.get("mode") === "move";
  const base = { sourceEditionBpaId, sourceEditionId, kinds, purpose, hub };
  if (!wantsMove) return { ...base, mode: "copy", notice: null, blocked: null };

  const reason = moveBlockedFor(context);
  if (!reason) return { ...base, mode: "move", notice: null, blocked: null };
  if (purpose && hub) {
    return { ...base, mode: "copy", notice: null, blocked: { reason, hub } };
  }
  return { ...base, mode: "copy", notice: reason, blocked: null };
}
